import { z } from "zod"

export const countryOptions = ["canada", "scotland"]
const charityNumberRegex: Record<(typeof countryOptions)[number], RegExp[]> = {
    canada: [/^\d{9}RR\d{4}$/],
    scotland: [/^SC0\d{5}$/, /^\d{7}$/],
}
const charityNumberExamples: Record<(typeof countryOptions)[number], string[]> =
    {
        canada: ["123456789RR0001"],
        scotland: ["SC012345", "1234567"],
    }

export const countryTelCodes = [
    {
        country: "canada",
        codes: ["+1"],
    },
    {
        country: "scotland",
        codes: ["+44"],
    },
]

const RegisterFormSchema = z
    .object({
        email: z
            .string()
            .email({ message: "Please enter a valid email address" }),
        firstName: z.string().min(2, { message: "First name is required" }),
        lastName: z.string().min(2, { message: "Last name is required" }),
        mobileNumber: z
            .string()
            .min(4, { message: "Mobile number is required" }),
        secondaryFirstName: z
            .string()
            .min(2, { message: "Secondary first name is required" }),
        secondaryLastName: z
            .string()
            .min(2, { message: "Secondary last name is required" }),
        secondaryEmail: z
            .string()
            .email({ message: "Please enter a valid secondary email address" }),
        password: z.string().min(2, { message: "Password is required" }),
        confirmPassword: z.string().min(2, { message: "Password is required" }),
        country: z.string().min(1, { message: "Please select a country" }),
        charityNumber: z
            .string()
            .min(1, { message: "Please enter a charity number" })
            .transform((val) => val.trim()),
        acceptTCs: z.boolean().refine((data) => data, {
            message: "Please accept the terms and conditions",
        }),
    })
    // do the passwords match?
    .refine((data) => data.password === data.confirmPassword, {
        message: "Passwords don't match",
        path: ["confirmPassword"],
    })
    .refine((data) => data.secondaryEmail !== data.email, {
        message: "Secondary email must belong to a different authorised person",
        path: ["secondaryEmail"],
    })
    // check the charity number format
    .refine(
        (data) => {
            const regex = charityNumberRegex[data.country]
            if (!regex?.length) {
                return true
            }
            return regex.some((r) =>
                r.test(data.charityNumber.replaceAll(" ", ""))
            )
        },
        (data) => ({
            message: `Please enter a valid charity number (e.g. ${charityNumberExamples[data.country].join(", ")})`,
            path: ["charityNumber"],
        })
    )
    // check the mobile number format
    .refine(
        (data) => {
            const country = countryTelCodes.find(
                (c) => c.country === data.country
            )
            if (!country) {
                return true
            }
            if (data.mobileNumber.startsWith("+")) {
                // if the data.mobileNumber doesn't start with a country code from the list, it's invalid
                return country.codes.some((c) =>
                    data.mobileNumber.startsWith(c)
                )
            }
            return true
        },
        {
            message: "Please enter a valid mobile number for your country",
            path: ["mobileNumber"],
        }
    )
    .transform((data) => {
        // remove non digit
        data.mobileNumber = data.mobileNumber.replace(/\D/g, "")
        // if they didn't put the international code in (should be the case), add it
        const country = countryTelCodes.find((c) => c.country === data.country)
        if (country) {
            if (!data.mobileNumber.startsWith("+")) {
                data.mobileNumber = `${country.codes[0]}${data.mobileNumber}`
            }
        }
        return data
    })

export { RegisterFormSchema }
